// スポット札 (通常入札 `SpotBidRow` / ブロック入札 `BlockBidRow`) → 約定結果集計の入力
// (`ExecutionSummaryRecord`) 変換。
//
// 集計・KPI の計算そのものは spot / intraday 共通の
// `~/components/bidding/common/executionSummary` が持ち、 ここは「スポット固有の
// 単位・ステータス写像」 だけを担う (= 時間前は同じ record 型へ別の写像を書く)。
import { BiddingBidSendStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { ExecutionSummaryRecord } from '~/components/bidding/common/executionSummary'
import type { BlockBidRow } from '~/pages/SpotBidding/models/blockBidRow'
import { deriveBlockBidStatusCategory } from '~/pages/SpotBidding/models/blockBidStatusCategory'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import {
  type SpotBidStatusCategory,
  deriveSpotBidStatusCategory,
} from '~/pages/SpotBidding/models/spotBidStatusCategory'

/**
 * 札を集計にどう入れるか (AC-BSP-002a / AC-BSP-002b)。 通常入札とブロック入札で共通。
 *   - counted: 約定結果が届いた札 (全約定 / 部分約定 / 未約定)。 件数と約定率の分母・分子に入れる
 *   - countOnly: JEPX に出した取消・失効の札。 入札件数には数え、 約定率の母集合から外す
 *   - omitted: JEPX に一度も出していない札と、 約定結果が未着の札 (約定待ち・締切後に JEPX が
 *     受け付けていたかを確かめている送信失敗)。 件数にも入札MW にも入れない。 未着の札は約定結果が
 *     届いた時点で counted になる
 */
type SummaryInclusion = 'counted' | 'countOnly' | 'omitted'

const RESULT_ARRIVED_CATEGORIES: ReadonlySet<SpotBidStatusCategory> = new Set([
  'allMatched',
  'partialMatched',
  'unmatched',
])

/**
 * JEPX に送った札か。 送信軸が JEPX に届いた (SENT)、 または届いたかを確かめ終えて失敗が
 * 確定した (SEND_FAILED / SEND_FAILED_FINAL) なら送っている。 送信中 (SENDING) のまま締切を
 * 迎えた札は JEPX が受け付けていたかを確かめている途中で、 約定結果が未着の札と同じく
 * 集計に入れない (§2 状態遷移の補足「送信中」、 AC-BSP-101)。
 *
 * TODO(#5590): 送信中から下書きに戻した後に失効にした札 (AC-BSP-002b では数える) は送信軸が
 * 送信前の値に戻るため、 ここでは送っていない側に入る。 JEPX の入札番号で判定できるようになれば
 * 直す。
 */
const isSentToJepx = (sendStatus: BiddingBidSendStatus | undefined): boolean =>
  sendStatus === BiddingBidSendStatus.SENT ||
  sendStatus === BiddingBidSendStatus.SEND_FAILED ||
  sendStatus === BiddingBidSendStatus.SEND_FAILED_FINAL

/** 約定結果 (全約定 / 部分約定 / 未約定) が届いた札の区分か。 */
export const hasExecutionResult = (category: SpotBidStatusCategory): boolean =>
  RESULT_ARRIVED_CATEGORIES.has(category)

const summaryInclusionOf = (
  category: SpotBidStatusCategory,
  sendStatus: BiddingBidSendStatus | undefined,
): SummaryInclusion => {
  if (hasExecutionResult(category)) return 'counted'
  if (category === 'cancelled' || category === 'expired') {
    return isSentToJepx(sendStatus) ? 'countOnly' : 'omitted'
  }
  return 'omitted'
}

/**
 * 通常入札の札を約定結果集計の入力へ変換する。
 *
 * 数量は換算しない。 コマ別数量の保持・書込 contract は **MW** で統一されており
 * (issue #4285。 `volumeMwh` / `execVolumeMwh` の `mwh` は旧 spec 時代の名残で格納値は
 * MW。 SoT: `~/models/komaQuantity` の module docstring)、 AC-BSP-002a / 002b /
 * AC-BSP-090 も MW 建てで定義されているので、 ここに MW↔MWh の境界変換は存在しない。
 */
export const toExecutionSummaryRecords = (
  rows: readonly SpotBidRow[],
  isPastDeadline: boolean,
): ExecutionSummaryRecord[] =>
  rows.flatMap((row) => {
    if (row.needsReview) return []
    const inclusion = summaryInclusionOf(
      deriveSpotBidStatusCategory(row, isPastDeadline),
      row.sendStatus,
    )
    if (inclusion === 'omitted') return []
    return [
      {
        areaCode: row.areaCode,
        // 売買方向は写すだけで既定値に倒さない (UNSPECIFIED を売に紛れさせず、 エリア×売買集計で
        // `TradeSideLabel` の fallback 表示として見せる)。
        side: row.direction,
        bidVolumeMw: Number(row.volumeMwh),
        execVolumeMw:
          row.execVolumeMwh !== undefined ? Number(row.execVolumeMwh) : 0,
        execPriceYenPerKwh:
          row.execPriceYenPerKwh !== undefined
            ? Number(row.execPriceYenPerKwh)
            : undefined,
        excluded: inclusion === 'countOnly',
      },
    ]
  })

/**
 * ブロック入札の Leg を約定結果集計の入力へ変換する (AC-BSP-002a / AC-BSP-002b)。
 *
 * - 1 Leg = 1 record で、 エリア × 売買の行の入札件数は Leg ごとに 1 件。 合計行は
 *   LINK / LOOP の 1 組を 1 件と数えるため、 グループの id を `groupKey` に入れる
 * - 入札量・約定量はコマごとの値の合計、 約定価格はコマごとの約定価格を約定量で加重した値。
 *   all-or-nothing のため全約定の Leg の約定量は各コマの入札量 (約定同期は約定価格が届いたコマに
 *   だけ約定量を書くため、 書かれていないコマも入札量で入れる)、 未約定の Leg は 0。 約定価格が未取得のコマの約定量は加重平均の
 *   重みに入れない (`pricedExecVolumeMw`)
 * - コマを絞り込んでいる時 (`komas` が空でない時) は選んだコマの分だけを入れる (AC-BSP-037)
 */
export const toBlockExecutionSummaryRecords = (
  legs: readonly BlockBidRow[],
  komas: ReadonlySet<number>,
  isPastDeadline: boolean,
): ExecutionSummaryRecord[] =>
  legs.flatMap((leg) => {
    if (leg.needsReview) return []
    const category = deriveBlockBidStatusCategory(leg, isPastDeadline)
    const inclusion = summaryInclusionOf(category, leg.sendStatus)
    if (inclusion === 'omitted') return []
    let bidVolumeMw = 0
    let execVolumeMw = 0
    let priceVolumeSum = 0
    let pricedExecVolumeMw = 0
    for (const [i, code] of leg.slotCodes.entries()) {
      if (komas.size > 0 && !komas.has(code)) continue
      const bidVolume = leg.slotVolumes[i] ?? leg.volumeMwh
      const execVolume =
        category === 'allMatched' ? bidVolume : (leg.slotExecVolumes[i] ?? 0)
      const execPrice = leg.slotExecPrices[i]
      bidVolumeMw += bidVolume
      execVolumeMw += execVolume
      if (execPrice !== undefined) {
        priceVolumeSum += execPrice * execVolume
        pricedExecVolumeMw += execVolume
      }
    }
    return [
      {
        areaCode: leg.areaCode,
        side: leg.direction,
        bidVolumeMw,
        execVolumeMw,
        execPriceYenPerKwh:
          pricedExecVolumeMw > 0
            ? priceVolumeSum / pricedExecVolumeMw
            : undefined,
        pricedExecVolumeMw,
        excluded: inclusion === 'countOnly',
        groupKey: leg.groupId,
      },
    ]
  })
